import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { reportUnexpectedError } from "../../error-reporter";
import { useMount } from "../../utils/custom-hooks";
import { LinkButton } from "../atoms/link-button";
import { ServiceProviderR } from "../pages/service-provider/routes";
import { SurveysServiceProviderR } from "../pages/surveys/service-provider/route";
import apiClient from "../../application/apis/api-client";
import { useFeatureFlag_EnableProductDataRemovalTasks } from "../../feature-flags";

export const ServiceProviderTabs = (org: { orgId: string }) => {
  const history = useHistory();
  const location = useLocation();
  const [selectedOrganization, setSelectedOrganization] = useState("");
  useMount(async () => {
    try {
      const { data: organization } = await apiClient.getOrganization(org.orgId);
      setSelectedOrganization(organization.name);
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("テナント情報の取得に失敗しました。");
    }
  });

  const ENABLE_PRODUCT_DATA_REMOVAL_TASKS =
    useFeatureFlag_EnableProductDataRemovalTasks(org.orgId);

  type TabItem = {
    label: string;
    link: string;
    hidden?: boolean; // FeatureFlag等によって表示制御したい場合に利用してください
  };
  const tabItems = useMemo<TabItem[]>(() => {
    return [
      {
        label: "基本情報",
        link: ServiceProviderR.Basic.link({ id: org.orgId }),
      },
      {
        label: "スマート申請（手続き）",
        link: ServiceProviderR.Procedures.link({ id: org.orgId }),
      },
      {
        label: "スマート申請（アーカイブ済み手続き）",
        link: ServiceProviderR.ArchivedProcedures.link({ id: org.orgId }),
      },
      {
        label: "スマート申請（申請者レビュー）",
        link: ServiceProviderR.CustomerReviews.link({ tenantId: org.orgId }),
      },
      {
        label: "アンケート",
        link: SurveysServiceProviderR.Surveys.link({ id: org.orgId }),
      },
      {
        label: "手続きマスタ",
        link: ServiceProviderR.ProcManagement.link({ orgId: org.orgId }),
      },
      {
        label: "窓口書類作成",
        link: ServiceProviderR.MadoguchiEditor.link({ orgId: org.orgId }),
      },
      {
        label: "窓口予約",
        link: ServiceProviderR.BoothReserve.link({ orgId: org.orgId }),
      },
      {
        label: "AI Studio",
        link: ServiceProviderR.AIStudio.link({ orgId: org.orgId }),
      },
      {
        label: "Call",
        link: ServiceProviderR.Call.link({ orgId: org.orgId }),
      },
      {
        label: "認定照会",
        link: ServiceProviderR.NinteiShoukai.link({ orgId: org.orgId }),
      },
      {
        label: "データ連携",
        link: ServiceProviderR.DataTransferTop.link({ tenantId: org.orgId }),
      },
      {
        label: "テナントユーザー",
        link: ServiceProviderR.OrgUsers.link({ orgId: org.orgId }),
      },
      {
        label: "サービスアカウント",
        link: ServiceProviderR.ServiceAccounts.link({ tenantId: org.orgId }),
      },
      {
        label: "グループ",
        link: ServiceProviderR.Groups.link({ id: org.orgId }),
      },
      {
        label: "IPアドレス設定",
        link: ServiceProviderR.ACL.link({ id: org.orgId }),
      },
      {
        label: "決済設定",
        link: ServiceProviderR.PaymentSetting.link({ id: org.orgId }),
      },
      {
        label: "製品設定",
        link: ServiceProviderR.Products.link({ id: org.orgId }),
      },
      {
        label: "製品データ削除",
        link: ServiceProviderR.ProductDataRemovalTasks.link({ id: org.orgId }),
        hidden: !ENABLE_PRODUCT_DATA_REMOVAL_TASKS,
      },
      {
        label: "GビズID設定",
        link: ServiceProviderR.GBizIDSetting.link({ id: org.orgId }),
      },
    ].filter((item) => !item.hidden);
  }, [org.orgId, ENABLE_PRODUCT_DATA_REMOVAL_TASKS]);

  if (!selectedOrganization) {
    return <div></div>;
  }

  return (
    <div className="field">
      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <h1 className="is-size-2">{selectedOrganization}の情報</h1>
          </div>
        </div>
      </nav>
      <div className="tabs">
        <ul
          className="is-flex-wrap-wrap"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            maxWidth: "100%",
            overflow: "visible",
          }}
        >
          {tabItems.map((item) => (
            <li
              key={item.label}
              className={location.pathname === item.link ? "is-active" : ""}
            >
              <LinkButton onClick={() => history.push(item.link)}>
                {item.label}
              </LinkButton>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
